import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { csvCell } from '../../lib/csvCell';
import { auth } from '../../lib/firebase';
import { stageBreakdown, stageCount, cumulativeStageFunnel, WORKFLOW_STAGES } from '../../lib/referrerStages';
import { BarChart3, Calendar, Download, Users, Briefcase, Building, Award, Plane, Landmark, CheckCircle, AlertCircle, Target, ArrowUpRight, ArrowDownRight, Eye, X, ChevronDown, Search, RefreshCw, FileText } from 'lucide-react';

type DateRange = 'today' | 'yesterday' | '7d' | '30d' | 'month' | 'lastMonth' | 'quarter' | 'lastQuarter' | 'year' | 'lastYear' | 'custom';

interface DateFilter {
  key: DateRange;
  label: string;
}

const DATE_FILTERS: DateFilter[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'lastQuarter', label: 'Last Quarter' },
  { key: 'year', label: 'This Year' },
  { key: 'lastYear', label: 'Last Year' },
  { key: 'custom', label: 'Custom Range' },
];

function getDateRange(key: DateRange): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now);
  switch (key) {
    case 'today': return { start: new Date(now.getFullYear(), now.getMonth(), now.getDate()), end: now };
    case 'yesterday': {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      return { start: new Date(y.getFullYear(), y.getMonth(), y.getDate()), end: new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59) };
    }
    case '7d': { const d = new Date(now); d.setDate(d.getDate() - 7); return { start: d, end: now }; }
    case '30d': { const d = new Date(now); d.setDate(d.getDate() - 30); return { start: d, end: now }; }
    case 'month': return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    case 'lastMonth': return { start: new Date(now.getFullYear(), now.getMonth() - 1, 1), end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59) };
    case 'quarter': return { start: new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1), end: now };
    case 'lastQuarter': { const q = Math.floor(now.getMonth() / 3) - 1; return { start: new Date(now.getFullYear(), q * 3, 1), end: new Date(now.getFullYear(), q * 3 + 3, 0, 23, 59, 59) }; }
    case 'year': return { start: new Date(now.getFullYear(), 0, 1), end: now };
    case 'lastYear': return { start: new Date(now.getFullYear() - 1, 0, 1), end: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59) };
    default: return { start: new Date(0), end: now };
  }
}

// Simple bar chart
const SimpleBar = ({ data, labels, color = 'bg-brand-primary', height = 32 }: { data: number[]; labels: string[]; color?: string; height?: number }) => {
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-2" style={{ minHeight: height + 20 }}>
      {data.map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[10px] font-semibold text-slate-700">{v}</span>
          <div className="w-full rounded-t-sm" style={{ height: `${(v / max) * height}px`, minHeight: v > 0 ? 4 : 0, backgroundColor: color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : color.replace('bg-', '') === 'emerald-500' ? '#10b981' : color.replace('bg-', '') === 'amber-500' ? '#f59e0b' : '#3b82f6' }} />
          <span className="text-[11px] text-slate-500 truncate max-w-[60px] text-center">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminReports({ allUsers, applications, programsList, canEdit = true }: { allUsers: any[]; applications: any[]; programsList?: any[]; canEdit?: boolean }) {
  const [dateFilter, setDateFilter] = useState<DateRange>('year');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [drillDown, setDrillDown] = useState<{ title: string; data: any[]; columns: string[] } | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Memoize dateRange to avoid infinite loop — getDateRange creates new Date objects every call
  const dateRange = useMemo(() => getDateRange(dateFilter), [dateFilter, customStart, customEnd]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`/api/admin/reports?start=${dateRange.start.toISOString()}&end=${dateRange.end.toISOString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setReportData(data);
        } else {
          // 404 or other server error — fall back to local data
          if (!cancelled) buildLocalReport();
        }
      } catch {
        if (!cancelled) buildLocalReport();
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [dateRange, applications]);

  const buildLocalReport = () => {
    const filtered = applications.filter((a: any) => {
      const created = a.createdAt ? new Date(a.createdAt) : null;
      return created && created >= dateRange.start && created <= dateRange.end;
    });
    // PR-12: milestone predicates aligned with the server endpoint
    // (/api/admin/reports) so this fallback path reports the same numbers
    // for the same records — previously `hired` missed accepted offers
    // and `started` missed 'active'. The stage-derived headline figures
    // (applicationsSubmitted / applicationsCompleted) are read off the
    // stageBreakdown partition below, exactly as the server does — never
    // re-counted from the legacy `submissionStatus` flag.
    const matched = filtered.filter((a: any) => a.matchedAt || a.assignment?.status);
    const shared = filtered.filter((a: any) => a.sharedWithEmployer);
    const interviewed = filtered.filter((a: any) => a.interviewStatus === 'scheduled' || a.interviewStatus === 'completed');
    const offered = filtered.filter((a: any) => a.offerStatus === 'sent' || a.offerStatus === 'accepted');
    const hired = filtered.filter((a: any) => a.placementStatus === 'Confirmed' || a.status === 'hired' || a.offerStatus === 'accepted');
    const visaApproved = filtered.filter((a: any) => a.visaStatus === 'approved');
    const flightsBooked = filtered.filter((a: any) => a.flightStatus === 'booked');
    const arrived = filtered.filter((a: any) => a.arrivalStatus === 'confirmed');
    const started = filtered.filter((a: any) => a.programStatus === 'started' || a.programStatus === 'active');

    // Program breakdown
    const programs: Record<string, any> = {};
    filtered.forEach((a: any) => {
      const prog = a.programType || 'Other';
      if (!programs[prog]) programs[prog] = { name: prog, applicants: 0, matched: 0, shared: 0, interviews: 0, offers: 0, hires: 0, visaApprovals: 0, programStarts: 0 };
      programs[prog].applicants++;
      if (matched.some((m: any) => m.applicationId === a.applicationId)) programs[prog].matched++;
      if (shared.some((s: any) => s.applicationId === a.applicationId)) programs[prog].shared++;
      if (interviewed.some((i: any) => i.applicationId === a.applicationId)) programs[prog].interviews++;
      if (offered.some((o: any) => o.applicationId === a.applicationId)) programs[prog].offers++;
      if (hired.some((h: any) => h.applicationId === a.applicationId)) programs[prog].hires++;
      if (visaApproved.some((va: any) => va.applicationId === a.applicationId)) programs[prog].visaApprovals++;
      if (started.some((s: any) => s.applicationId === a.applicationId)) programs[prog].programStarts++;
    });

    const stageRows = stageBreakdown(filtered);

    setReportData({
      totalApplications: filtered.length,
      applicationsSubmitted: stageCount(stageRows, 'submitted'),
      applicationsCompleted: stageCount(stageRows, 'closed'),
      matched,
      profilesShared: shared.length,
      interviewed: interviewed.length,
      offersSent: offered.length,
      hired: hired.length,
      visaApproved: visaApproved.length,
      flightsBooked: flightsBooked.length,
      arrived: arrived.length,
      programsStarted: started.length,
      // §4.1: the by-stage breakdown is a partition over the canonical 8
      // workflow stages (single-sourced via stageBreakdown) — every
      // application lands in exactly one stage, so the shares sum to
      // 100%. Milestone counts (matched, shared, hired, visa, …) stay as
      // their own KPI fields above; they are metrics, not stages.
      applicantsByStage: Object.fromEntries(
        stageRows.map(({ label, count }) => [label, count]),
      ),
      programs: Object.values(programs),
      monthlyData: {
        labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        values: Array.from({ length: 12 }, (_, i) => filtered.filter((a: any) => {
          const d = a.createdAt ? new Date(a.createdAt) : null;
          return d && d.getMonth() === i && d.getFullYear() === new Date().getFullYear();
        }).length),
      },
    });
  };

  const exportCSV = (data: any[], filename: string) => {
    if (!data.length) return;
    const keys = Object.keys(data[0]);
    const csv = [keys.map(csvCell).join(','), ...data.map(r => keys.map(k => csvCell(r[k])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename;
    a.click(); URL.revokeObjectURL(url);
  };

  const KpiCard = ({ label, value, icon: Icon, color, onClick }: { label: string; value: string | number; icon: any; color: string; onClick?: () => void }) => (
    <div onClick={onClick} className={`bg-white rounded-2xl p-4 shadow-sm ring-1 ring-slate-100 ${onClick ? 'cursor-pointer hover:shadow-md hover:ring-blue-200 transition' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${color.replace('text-', 'bg-')}/10`}>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
      </div>
      <p className="text-lg font-bold text-slate-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-[11px] text-slate-500 font-medium">{label}</p>
    </div>
  );

  if (loading) {
    return <div className="p-8 text-center"><div className="premium-loader mx-auto" /><p className="text-sm text-slate-500 mt-4">Loading reports...</p></div>;
  }

  if (drillDown) {
    return (
      <div className="p-6 animate-fade-in">
        <button onClick={() => setDrillDown(null)} className="flex items-center gap-1 text-sm font-semibold text-brand mb-4 cursor-pointer hover:underline">&larr; Back to Reports</button>
        <h2 className="text-lg font-bold text-slate-900 mb-4">{drillDown.title}</h2>
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-semibold">
              <tr>{drillDown.columns.map(c => <th key={c} className="px-4 py-3 text-left">{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {drillDown.data.slice(0, 100).map((row: any, i: number) => (
                <tr key={i} className="hover:bg-slate-50"><td className="px-4 py-2 text-slate-700" colSpan={drillDown.columns.length}>{JSON.stringify(row).slice(0, 200)}</td></tr>
              ))}
              {drillDown.data.length === 0 && <tr><td colSpan={drillDown.columns.length} className="px-4 py-6 text-center text-slate-500">No records found.</td></tr>}
            </tbody>
          </table>
        </div>
        {canEdit && (
          <button onClick={() => exportCSV(drillDown.data.slice(0, 5000), `${drillDown.title.replace(/\s+/g, '_')}.csv`)} className="mt-4 px-4 py-2 btn-primary text-white rounded-xl text-sm font-semibold flex items-center gap-2 cursor-pointer">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        )}
      </div>
    );
  }

  const rd = reportData || { totalApplications: 0, applicationsSubmitted: 0, applicationsCompleted: 0, profilesShared: 0, interviewed: 0, offersSent: 0, hired: 0, visaApproved: 0, flightsBooked: 0, arrived: 0, programsStarted: 0, applicantsByStage: {}, programs: [], monthlyData: { labels: [], values: [] } };

  // PR-12: stage-derived figures are read off the SAME applicantsByStage
  // partition the "Applicants by Stage" section below renders — on either
  // data path (server payload or local fallback) — so the KPI cards and
  // the Recruitment Funnel can never contradict that section. (The old
  // "Submitted" / "Completed" KPIs re-counted the legacy submissionStatus
  // flag, which the workflow engine does not advance: 6 vs the 12 records
  // actually in Submitted for Review, and 0 vs 1 in Closure.)
  const stageRows = WORKFLOW_STAGES.map(({ key, label }) => ({
    stage: key,
    label,
    count: Number(rd.applicantsByStage?.[label]) || 0,
  }));
  const funnelRows = cumulativeStageFunnel(stageRows).filter(r => r.stage !== 'draft');

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 md:p-6 border-b border-slate-200 bg-white shrink-0 flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Reports</h1>
          <p className="text-xs text-slate-500">Platform analytics and performance metrics</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <select value={dateFilter} onChange={e => setDateFilter(e.target.value as DateRange)}
              className="appearance-none pl-3 pr-8 py-2 text-xs font-semibold border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 cursor-pointer">
              {DATE_FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          </div>
          {dateFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-200 rounded-xl" />
              <span className="text-xs text-slate-500">to</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-200 rounded-xl" />
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
        {/* KPI Cards — Pipeline: counts by current workflow stage, read off
            the same canonical partition as "Applicants by Stage" below. */}
        <div>
          <h3 className="text-sm font-bold text-slate-900">Applicant Pipeline</h3>
          <p className="text-xs text-slate-500 mb-3">Applications by current workflow stage — these always match the Applicants by Stage breakdown below.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard label="Total Applications" value={rd.totalApplications} icon={FileText} color="text-brand" onClick={() => setDrillDown({ title: 'All Applications', data: applications.slice(0, 500), columns: ['Data'] })} />
            <KpiCard label="Submitted for Review" value={stageCount(stageRows, 'submitted')} icon={CheckCircle} color="text-green-600" />
            <KpiCard label="Closure" value={stageCount(stageRows, 'closed')} icon={Award} color="text-emerald-600" />
          </div>
        </div>

        {/* KPI Cards — Hiring & Milestones: cumulative event/milestone
            counts (an application can count toward several). These are NOT
            workflow stages and do not sum to the total. */}
        <div>
          <h3 className="text-sm font-bold text-slate-900">Hiring &amp; Milestones</h3>
          <p className="text-xs text-slate-500 mb-3">Cumulative hiring events and applicant milestones across all applications in range — not pipeline stages.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Shared with Employers" value={rd.profilesShared} icon={Users} color="text-purple-600" />
            <KpiCard label="Interviews" value={rd.interviewed} icon={Target} color="text-amber-600" />
            <KpiCard label="Offers Sent" value={rd.offersSent} icon={Briefcase} color="text-indigo-600" />
            <KpiCard label="Hired" value={rd.hired} icon={CheckCircle} color="text-green-700" />
            <KpiCard label="Visa Approved" value={rd.visaApproved} icon={Landmark} color="text-brand" />
            <KpiCard label="Flights Booked" value={rd.flightsBooked} icon={Plane} color="text-sky-600" />
            <KpiCard label="Arrived" value={rd.arrived} icon={Award} color="text-teal-600" />
            <KpiCard label="Programs Started" value={rd.programsStarted} icon={Award} color="text-lime-600" />
          </div>
        </div>

        {/* Applications by Month */}
        <div className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100">
          <h3 className="text-sm font-bold text-slate-900 mb-4">Applications by Month</h3>
          <SimpleBar data={rd.monthlyData.values} labels={rd.monthlyData.labels} color="bg-brand-primary" height={40} />
        </div>

        {/* Applicant Pipeline */}
        <div className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-900">Applicants by Stage</h3>
            <button onClick={() => exportCSV(Object.entries(rd.applicantsByStage).map(([k, v]) => ({ Stage: k, Count: v })), 'pipeline.csv')} className="text-xs text-brand font-semibold hover:underline flex items-center gap-1 cursor-pointer"><Download className="h-3 w-3" /> Export</button>
          </div>
          <div className="space-y-3">
            {Object.entries(rd.applicantsByStage).map(([stage, count]) => {
              const total = Object.values(rd.applicantsByStage).reduce((a: number, b: any) => a + (typeof b === 'number' ? b : 0), 0);
              const numCount = Number(count) || 0;
              const pct = total > 0 ? ((numCount / total) * 100).toFixed(1) : '0';
              return (
                <div key={stage} className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-slate-700 w-40 shrink-0">{stage}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-brand-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-slate-600 w-8 text-right">{count}</span>
                  <span className="text-[11px] text-slate-500 w-10 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Program Performance */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-x-auto">
          <div className="p-5 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900">Program Performance</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-semibold">
              <tr><th className="px-4 py-3 text-left">Program</th><th className="px-4 py-3 text-right">Applicants</th><th className="px-4 py-3 text-right">Matched</th><th className="px-4 py-3 text-right">Shared</th><th className="px-4 py-3 text-right">Interviews</th><th className="px-4 py-3 text-right">Offers</th><th className="px-4 py-3 text-right">Hires</th><th className="px-4 py-3 text-right">Visa Approved</th><th className="px-4 py-3 text-right">Started</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(rd.programs || []).length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-500">No program data available.</td></tr>}
              {(rd.programs || []).map((p: any, i: number) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{p.name}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{p.applicants}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{p.matched}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{p.shared}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{p.interviews}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{p.offers}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{p.hires}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{p.visaApprovals}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{p.programStarts}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-3 border-t border-slate-100">
            <button onClick={() => exportCSV(rd.programs, 'program_performance.csv')} className="text-xs text-brand font-semibold hover:underline flex items-center gap-1 cursor-pointer"><Download className="h-3 w-3" /> Export CSV</button>
          </div>
        </div>

        {/* Referrer, Employer, Country reports - wire to API when ready */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100">
            <h3 className="text-sm font-bold text-slate-900 mb-2">Referrer & Employer Reports</h3>
            <p className="text-xs text-slate-500 mb-4">Detailed reports for referrers, employers, countries, and visa tracking can be generated.</p>
            {canEdit && (
              <button onClick={() => exportCSV(applications.slice(0, 5000).map((a: any) => ({
                Applicant: `${a.personalDetails?.firstName || ''} ${a.personalDetails?.lastName || ''}`,
                Program: a.programType || '',
                Status: a.submissionStatus || '',
                Referrer: a.referredBy || a.referrerName || 'N/A',
              })), 'applicant_report.csv')} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition cursor-pointer flex items-center gap-2">
                <Download className="h-3.5 w-3.5" /> Export Full Report (CSV)
              </button>
            )}
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm ring-1 ring-slate-100">
            <h3 className="text-sm font-bold text-slate-900 mb-2">Recruitment Funnel</h3>
            <p className="text-xs text-slate-500 mb-3">How many applications have reached each workflow stage or beyond — derived from the same stage breakdown as Applicants by Stage.</p>
            {(() => {
              const total = rd.totalApplications || 1;
              // PR-12: the funnel is canonical stage progression (cumulative
              // "reached this stage or beyond" off the stageBreakdown
              // partition), not the retired milestone mix — the old funnel's
              // "Completed 0" contradicted the Closure stage's 1 on the
              // same page. Hiring events live in Hiring & Milestones above.
              const stages = [
                { label: 'Applications', value: rd.totalApplications },
                ...funnelRows.map(r => ({
                  label: r.stage === 'closed' ? r.label : `${r.label} or beyond`,
                  value: r.count,
                })),
              ];
              return (
                <div className="space-y-2">
                  {stages.map((s, i) => {
                    const pct = ((s.value / total) * 100).toFixed(1);
                    const conversion = i > 0 ? ((s.value / Math.max(stages[i - 1].value, 1)) * 100).toFixed(1) : '100.0';
                    return (
                      <div key={s.label} className="flex items-center gap-3 text-xs">
                        <span className="text-slate-600 w-24">{s.label}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-2">
                          <div className="h-full bg-brand-primary rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-semibold text-slate-700 w-10 text-right">{s.value}</span>
                        {i > 0 && <span className="text-[10px] text-emerald-600 w-14 text-right">{conversion}% conv.</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
